import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX, FiArrowRight, FiFileText, FiUser, FiDollarSign, FiCalendar, FiClipboard, FiCheckSquare, FiNavigation } from 'react-icons/fi';
import { searchEngine, SearchResult } from '../../lib/search/searchEngine';
import './SitewideSearch.css';

const SERVICE_ICONS: Record<string, React.ReactNode> = {
  'Contacts': <FiUser />,
  'Accounting': <FiDollarSign />,
  'Documents': <FiFileText />,
  'Calendar': <FiCalendar />,
  'Tax Filing': <FiClipboard />,
  'Tasks': <FiCheckSquare />,
  'Navigation': <FiNavigation />,
};

const SitewideSearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Map<string, SearchResult[]>>(new Map());
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // The bar is fixed over the bottom of every page: publish its height as
  // --sitewide-search-h so bars that stick to the bottom (e.g. Create accounts in
  // Setup GL Accounts) sit above it instead of under it, where clicks never reach them.
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty('--sitewide-search-h', `${bar.offsetHeight}px`);
    publish();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    ro?.observe(bar);
    return () => { ro?.disconnect(); root.style.removeProperty('--sitewide-search-h'); };
  }, []);

  // Flatten results for keyboard navigation
  const flatResults = React.useMemo(() => {
    const flat: SearchResult[] = [];
    results.forEach(group => flat.push(...group));
    return flat;
  }, [results]);

  // Search on query change (debounced)
  useEffect(() => {
    if (!query.trim()) {
      setResults(new Map());
      setSelectedIndex(-1);
      return;
    }

    const timer = setTimeout(() => {
      const grouped = searchEngine.searchGrouped(query, 20);
      setResults(grouped);
      setSelectedIndex(-1);
    }, 50); // 50ms debounce — fast enough to feel instant

    return () => clearTimeout(timer);
  }, [query]);

  // Navigate to result
  const handleSelect = useCallback((result: SearchResult) => {
    navigate(result.item.path);
    setQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  }, [navigate]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0 && flatResults[selectedIndex]) {
      e.preventDefault();
      handleSelect(flatResults[selectedIndex]);
    } else if (e.key === 'Escape') {
      setQuery('');
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Keyboard shortcut: Ctrl+K or Cmd+K to focus
  useEffect(() => {
    const handleGlobalKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    };
    document.addEventListener('keydown', handleGlobalKey);
    return () => document.removeEventListener('keydown', handleGlobalKey);
  }, []);

  const hasResults = results.size > 0;
  const totalResults = flatResults.length;

  return (
    <div className="sitewide-search" ref={panelRef}>
      {/* Results panel (pops up above the bar) */}
      {isOpen && hasResults && (
        <div className="sitewide-results">
          {Array.from(results.entries()).map(([service, items]) => (
            <div key={service} className="sitewide-results-group">
              <div className="sitewide-results-header">
                <span className="sitewide-results-icon">{SERVICE_ICONS[service] || <FiSearch />}</span>
                <span>{service}</span>
                <span className="sitewide-results-count">{items.length}</span>
              </div>
              {items.map((result, idx) => {
                const globalIdx = flatResults.indexOf(result);
                return (
                  <button
                    key={result.item.id}
                    className={`sitewide-result-item ${globalIdx === selectedIndex ? 'selected' : ''}`}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIndex(globalIdx)}
                  >
                    <div className="sitewide-result-text">
                      <span className="sitewide-result-title">{result.item.title}</span>
                      {result.item.subtitle && (
                        <span className="sitewide-result-subtitle">{result.item.subtitle}</span>
                      )}
                    </div>
                    <span className="sitewide-result-type">{result.item.type}</span>
                    <FiArrowRight className="sitewide-result-arrow" />
                  </button>
                );
              })}
            </div>
          ))}
          <div className="sitewide-results-footer">
            {totalResults} result{totalResults !== 1 ? 's' : ''} found
          </div>
        </div>
      )}

      {/* No results */}
      {isOpen && query.trim() && !hasResults && (
        <div className="sitewide-results">
          <div className="sitewide-no-results">
            No results for "{query}"
          </div>
        </div>
      )}

      {/* Search input bar */}
      <div className="sitewide-search-bar" ref={barRef}>
        <FiSearch className="sitewide-search-icon" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search everything... (Ctrl+K)"
          value={query}
          onChange={e => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="sitewide-search-input"
        />
        {query && (
          <button className="sitewide-search-clear" onClick={() => { setQuery(''); setResults(new Map()); }}>
            <FiX />
          </button>
        )}
        <kbd className="sitewide-kbd">Ctrl+K</kbd>
      </div>
    </div>
  );
};

export default SitewideSearch;
